import { config } from './configuration';

type Kind = 'render' | 'error' | 'unhandledrejection';

// Per page load: the same error is sent once, and at most MAX_PER_PAGE in all,
// so a crash inside a render loop cannot flood the endpoint.
const MAX_PER_PAGE = 10;
const sent = new Set<string>();
let currentUserId: string | undefined;

export function setClientErrorUser(userId: string | null | undefined) {
  currentUserId = userId ?? undefined;
}

/**
 * Tell the backend a browser error happened (POST /api/client-errors). Never
 * throws and never retries — reporting must not become a second failure.
 */
export function reportClientError(kind: Kind, error: unknown) {
  if (typeof window === 'undefined') return;
  const message =
    error instanceof Error ? error.message : typeof error === 'string' ? error : String(error);
  // Noise with nothing to act on: cross-origin scripts hide their message, and
  // ResizeObserver's loop warning is benign.
  if (!message || message === 'Script error.' || message.includes('ResizeObserver loop')) return;

  const key = `${kind}:${message}`;
  if (sent.has(key) || sent.size >= MAX_PER_PAGE) return;
  sent.add(key);

  const body = JSON.stringify({
    kind,
    message: message.slice(0, 1000),
    stack: error instanceof Error ? error.stack?.slice(0, 4000) : undefined,
    url: window.location.pathname.slice(0, 500),
    userId: currentUserId,
  });
  fetch(`${config.apiUrl}/client-errors`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
  }).catch(() => {});
}

/** Errors that never reach an error boundary: event handlers, timers, promises. */
export function installClientErrorListeners(): () => void {
  const onError = (event: ErrorEvent) => {
    // Browser extensions inject scripts into every page; their errors are not ours.
    if (event.filename && !event.filename.startsWith(window.location.origin)) return;
    reportClientError('error', event.error ?? event.message);
  };
  const onRejection = (event: PromiseRejectionEvent) => {
    reportClientError('unhandledrejection', event.reason);
  };
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
  };
}
